import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import RemoveDoneIcon from '@mui/icons-material/RemoveDone';
import { useUser } from '../../hooks/useUser';
import { getCallableUsers } from '../../services/call';
import { createDocumentTag, listDocumentTags } from '../../services/documentTags';
import {
    assignDocument,
    deleteDocument,
    downloadDocument,
    listDocumentCaregivers,
    listDocuments,
    unassignDocument,
    updateDocument,
    updateDocumentTags,
    uploadDocument
} from '../../services/documents';

const PAGE_LIMIT = 5;
const BULK_FETCH_LIMIT = 50;
const MINE_SCOPE = 'mine';
const ASSIGNED_SCOPE = 'assigned';
const TEAM_SCOPE = 'team';
const NO_TAGS_FILTER = '__NO_TAGS__';

const dialogPaperSx = {
    borderRadius: '22px',
    boxShadow: '0px 10px 30px rgba(61, 61, 61, 0.12)'
};

const dialogTitleSx = {
    px: 3,
    pt: 3,
    pb: 1.5,
    fontWeight: 700,
    color: '#3D3D3D'
};

const dialogContentSx = {
    px: 3,
    pb: 1,
    pt: 0.5
};

const dialogActionsSx = {
    px: 3,
    pb: 2.5,
    pt: 1,
    gap: 1
};

const secondaryDialogButtonSx = {
    minHeight: 48,
    px: 2.2,
    textTransform: 'none',
    fontWeight: 700,
    color: '#3D3D3D',
    borderColor: '#D9D9D9',
    borderWidth: '2px',
    borderRadius: '16px',
    boxShadow: 'none',
    '&:hover': {
        bgcolor: '#F7F7F7',
        borderColor: '#CFCFCF',
        borderWidth: '2px',
        boxShadow: 'none'
    }
};

const primaryDialogButtonSx = {
    minHeight: 48,
    px: 2.2,
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: '16px',
    bgcolor: '#3D3D3D',
    boxShadow: 'none',
    '&:hover': {
        bgcolor: '#3D3D3D',
        boxShadow: 'none'
    }
};

const sectionCardSx = {
    p: { xs: 1.8, md: 2.2 },
    bgcolor: '#FFFFFF',
    borderRadius: '20px',
    border: '1px solid #ECECEC',
    boxShadow: 'none'
};

const tabButtonSx = (active) => ({
    minHeight: 46,
    px: 2.4,
    borderRadius: '14px',
    border: active ? '2px solid #D63731' : '2px solid #E2E2E2',
    bgcolor: active ? 'rgba(214, 55, 49, 0.05)' : '#FFFFFF',
    color: active ? '#D63731' : '#3D3D3D',
    fontWeight: 700,
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': {
        boxShadow: 'none',
        bgcolor: active ? 'rgba(214, 55, 49, 0.08)' : '#F7F7F7'
    }
});

const actionIconButtonSx = {
    width: 40,
    height: 40,
    borderRadius: '12px',
    border: '1px solid #E2E2E2',
    color: '#3D3D3D',
    bgcolor: '#FFFFFF',
    '&:hover': {
        bgcolor: '#F7F7F7'
    }
};

const filterActionButtonSx = {
    width: 38,
    height: 38,
    borderRadius: '50%',
    border: '1px solid #E2E2E2',
    color: '#3D3D3D',
    bgcolor: '#FFFFFF',
    '&:hover': {
        bgcolor: '#F7F7F7'
    }
};

const uploadDropzoneSx = {
    minHeight: 156,
    borderRadius: '18px',
    border: '1px dashed #D63731',
    bgcolor: '#FCFCFC',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1.2,
    cursor: 'pointer',
    px: 2,
    textAlign: 'center',
    transition: 'all 0.2s ease',
    '&:hover': {
        bgcolor: '#F7F7F7',
        borderColor: '#D63731'
    }
};

const selectMenuProps = {
    PaperProps: {
        sx: {
            maxHeight: 320
        }
    }
};

const normalizeTagLabel = (tag) => tag?.tag || tag?.name || '';
const hasNoTagsFilter = (selected) => selected.includes(NO_TAGS_FILTER);
const realSelectedTags = (selected) => selected.filter((value) => value !== NO_TAGS_FILTER);
const matchesSelectedTags = (doc, selected) => {
    if (!selected.length) return false;

    const docTagUuids = (doc?.tags || []).map((tag) => tag.uuid).filter(Boolean);
    const realTags = realSelectedTags(selected);
    const tagMatch = realTags.some((uuid) => docTagUuids.includes(uuid));
    const noTagsMatch = hasNoTagsFilter(selected) && docTagUuids.length === 0;

    return tagMatch || noTagsMatch;
};

const DocumentsWorkspace = () => {
    const { user } = useUser();
    const dropInputRef = useRef(null);
    const tagsInitializedRef = useRef(false);
    const mineEffectKeyRef = useRef('');
    const assignedEffectKeyRef = useRef('');
    const teamEffectKeyRef = useRef('');

    const [scope, setScope] = useState(MINE_SCOPE);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [tags, setTags] = useState([]);
    const [selectedTagsByScope, setSelectedTagsByScope] = useState({
        [MINE_SCOPE]: [],
        [ASSIGNED_SCOPE]: [],
        [TEAM_SCOPE]: []
    });

    const [caregivers, setCaregivers] = useState([]);
    const [selectedCaregiverUuid, setSelectedCaregiverUuid] = useState('');
    const [teamMembers, setTeamMembers] = useState([]);
    const [selectedTeamMemberUuid, setSelectedTeamMemberUuid] = useState('');

    const [mineDocs, setMineDocs] = useState([]);
    const [minePageIndex, setMinePageIndex] = useState(0);

    const [assignedDocs, setAssignedDocs] = useState([]);
    const [assignedPageIndex, setAssignedPageIndex] = useState(0);
    const [teamDocs, setTeamDocs] = useState([]);
    const [teamPageIndex, setTeamPageIndex] = useState(0);

    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [uploadFileObj, setUploadFileObj] = useState(null);
    const [uploadDocumentName, setUploadDocumentName] = useState('');
    const [uploadHidden, setUploadHidden] = useState(false);
    const [uploadTags, setUploadTags] = useState([]);
    const [uploadNewTag, setUploadNewTag] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');

    const [createTagDialogOpen, setCreateTagDialogOpen] = useState(false);
    const [newTagName, setNewTagName] = useState('');
    const [tagSubmitting, setTagSubmitting] = useState(false);
    const [tagError, setTagError] = useState('');

    const [documentTagsDialogOpen, setDocumentTagsDialogOpen] = useState(false);
    const [documentAssignDialogOpen, setDocumentAssignDialogOpen] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [documentTagsEdit, setDocumentTagsEdit] = useState([]);
    const [documentCaregiverUuid, setDocumentCaregiverUuid] = useState('');
    const [documentActionLoading, setDocumentActionLoading] = useState(false);
    const [documentActionError, setDocumentActionError] = useState('');
    const [documentAssignedCaregivers, setDocumentAssignedCaregivers] = useState([]);
    const [loadingAssignedCaregivers, setLoadingAssignedCaregivers] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletingDocument, setDeletingDocument] = useState(false);

    const selectedTagUuids = selectedTagsByScope[scope] || [];
    const allTagUuids = useMemo(() => tags.map((tag) => tag.uuid), [tags]);
    const allTagFilterValues = useMemo(() => [...allTagUuids, NO_TAGS_FILTER], [allTagUuids]);
    const visibleMineDocs = useMemo(() => {
        const start = minePageIndex * PAGE_LIMIT;
        return mineDocs.slice(start, start + PAGE_LIMIT);
    }, [mineDocs, minePageIndex]);

    const mineHasNextPage = (minePageIndex + 1) * PAGE_LIMIT < mineDocs.length;
    const visibleAssignedDocs = useMemo(() => {
        const start = assignedPageIndex * PAGE_LIMIT;
        return assignedDocs.slice(start, start + PAGE_LIMIT);
    }, [assignedDocs, assignedPageIndex]);
    const assignedHasNextPage = (assignedPageIndex + 1) * PAGE_LIMIT < assignedDocs.length;
    const visibleTeamDocs = useMemo(() => {
        const start = teamPageIndex * PAGE_LIMIT;
        return teamDocs.slice(start, start + PAGE_LIMIT);
    }, [teamDocs, teamPageIndex]);
    const teamHasNextPage = (teamPageIndex + 1) * PAGE_LIMIT < teamDocs.length;

    const activeDocs = scope === MINE_SCOPE ? visibleMineDocs : scope === ASSIGNED_SCOPE ? visibleAssignedDocs : visibleTeamDocs;
    const hasNextPage = scope === MINE_SCOPE ? mineHasNextPage : scope === ASSIGNED_SCOPE ? assignedHasNextPage : teamHasNextPage;
    const pageIndex = scope === MINE_SCOPE ? minePageIndex : scope === ASSIGNED_SCOPE ? assignedPageIndex : teamPageIndex;
    const totalDocsForScope =
        scope === MINE_SCOPE ? mineDocs.length : scope === ASSIGNED_SCOPE ? assignedDocs.length : teamDocs.length;
    const totalPages = Math.max(1, Math.ceil(totalDocsForScope / PAGE_LIMIT));

    const setSelectedTagsForScope = useCallback((targetScope, nextValue) => {
        setSelectedTagsByScope((prev) => ({
            ...prev,
            [targetScope]: nextValue
        }));
    }, []);

    useEffect(() => {
        const loadTags = async () => {
            if (!user.selectedRole) return;
            try {
                const response = await listDocumentTags({
                    userRoleUuid: user.selectedRole,
                    paginationLimit: 50
                });
                const rows = Array.isArray(response?.data) ? response.data : [];
                const loadedUuids = rows.map((tag) => tag.uuid);
                setTags(rows);

                setSelectedTagsByScope((prev) => {
                    if (!tagsInitializedRef.current) {
                        tagsInitializedRef.current = true;
                        return {
                            [MINE_SCOPE]: [...loadedUuids, NO_TAGS_FILTER],
                            [ASSIGNED_SCOPE]: [...loadedUuids, NO_TAGS_FILTER],
                            [TEAM_SCOPE]: [...loadedUuids, NO_TAGS_FILTER]
                        };
                    }

                    return {
                        [MINE_SCOPE]: (prev[MINE_SCOPE] || []).filter(
                            (uuid) => uuid === NO_TAGS_FILTER || loadedUuids.includes(uuid)
                        ),
                        [ASSIGNED_SCOPE]: (prev[ASSIGNED_SCOPE] || []).filter(
                            (uuid) => uuid === NO_TAGS_FILTER || loadedUuids.includes(uuid)
                        ),
                        [TEAM_SCOPE]: (prev[TEAM_SCOPE] || []).filter(
                            (uuid) => uuid === NO_TAGS_FILTER || loadedUuids.includes(uuid)
                        )
                    };
                });
            } catch {
                setTags([]);
            }
        };

        void loadTags();
    }, [user.selectedRole]);

    useEffect(() => {
        const loadCaregivers = async () => {
            if (!user.selectedRole) return;
            try {
                let paginationToken = null;
                let nextToken = null;
                const collected = [];
                const seenTokens = new Set();

                do {
                    if (paginationToken && seenTokens.has(paginationToken)) {
                        break;
                    }
                    if (paginationToken) {
                        seenTokens.add(paginationToken);
                    }

                    const response = await getCallableUsers({
                        userRoleUuid: user.selectedRole,
                        roleFilters: ['PATIENT_CARE_GIVER'],
                        paginationToken,
                        paginationLimit: 50
                    });

                    const rows = Array.isArray(response?.users) ? response.users : [];
                    collected.push(...rows);

                    nextToken = response?.nextToken || null;
                    paginationToken = nextToken;
                } while (nextToken);

                const uniqueCaregivers = collected.filter(
                    (item, index, array) =>
                        item?.userId &&
                        array.findIndex((candidate) => candidate?.userId === item.userId) === index
                );

                setCaregivers(uniqueCaregivers);
            } catch {
                setCaregivers([]);
            }
        };

        void loadCaregivers();
    }, [user.selectedRole]);

    useEffect(() => {
        const loadTeamMembers = async () => {
            if (!user.selectedRole) return;
            try {
                let paginationToken = null;
                let nextToken = null;
                const collected = [];
                const seenTokens = new Set();

                do {
                    if (paginationToken && seenTokens.has(paginationToken)) {
                        break;
                    }
                    if (paginationToken) {
                        seenTokens.add(paginationToken);
                    }

                    const response = await getCallableUsers({
                        userRoleUuid: user.selectedRole,
                        roleFilters: ['ACCOUNT_ADMIN', 'CLINIC_CARE_GIVER'],
                        includeCurrentUser: true,
                        paginationToken,
                        paginationLimit: 50
                    });

                    const rows = Array.isArray(response?.users) ? response.users : [];
                    collected.push(...rows);

                    nextToken = response?.nextToken || null;
                    paginationToken = nextToken;
                } while (nextToken);

                const uniqueTeamMembers = collected.filter(
                    (item, index, array) =>
                        item?.userId &&
                        array.findIndex((candidate) => candidate?.userId === item.userId) === index
                );

                setTeamMembers(uniqueTeamMembers);
            } catch {
                setTeamMembers([]);
            }
        };

        void loadTeamMembers();
    }, [user.selectedRole]);

    useEffect(() => {
        if (scope !== ASSIGNED_SCOPE) return;
        if (selectedCaregiverUuid) return;
        if (!caregivers.length) return;

        setSelectedCaregiverUuid(caregivers[0]?.userId || '');
    }, [caregivers, scope, selectedCaregiverUuid]);

    useEffect(() => {
        if (scope !== TEAM_SCOPE) return;
        if (selectedTeamMemberUuid) return;
        if (!teamMembers.length) return;

        setSelectedTeamMemberUuid(teamMembers[0]?.userId || '');
    }, [scope, selectedTeamMemberUuid, teamMembers]);

    useEffect(() => {
        setMinePageIndex(0);
    }, [selectedTagsByScope.mine]);

    useEffect(() => {
        setAssignedPageIndex(0);
    }, [selectedCaregiverUuid, selectedTagsByScope.assigned]);

    useEffect(() => {
        setTeamPageIndex(0);
    }, [selectedTeamMemberUuid, selectedTagsByScope.team]);

    const createTag = useCallback(
        async (tagName, onCreated) => {
            if (!user.selectedRole || !tagName.trim()) return;

            setTagSubmitting(true);
            setTagError('');

            try {
            const response = await createDocumentTag({
                userRoleUuid: user.selectedRole,
                tag: tagName.trim()
            });

            const created = response?.data;
            if (!created?.uuid) return;

            setTags((prev) => [...prev, created]);
            setSelectedTagsByScope((prev) => ({
                [MINE_SCOPE]: (prev[MINE_SCOPE] || []).includes(created.uuid)
                    ? prev[MINE_SCOPE]
                    : [...(prev[MINE_SCOPE] || []), created.uuid],
                [ASSIGNED_SCOPE]: (prev[ASSIGNED_SCOPE] || []).includes(created.uuid)
                    ? prev[ASSIGNED_SCOPE]
                    : [...(prev[ASSIGNED_SCOPE] || []), created.uuid]
                ,
                [TEAM_SCOPE]: (prev[TEAM_SCOPE] || []).includes(created.uuid)
                    ? prev[TEAM_SCOPE]
                    : [...(prev[TEAM_SCOPE] || []), created.uuid]
            }));

            if (typeof onCreated === 'function') {
                onCreated(created);
            }
            } catch (err) {
                setTagError(err?.response?.data?.description || 'Nao foi possivel criar a tag.');
            } finally {
                setTagSubmitting(false);
            }
        },
        [user.selectedRole]
    );

    const loadMineDocuments = useCallback(async () => {
        if (!user.selectedRole) return;

        if (selectedTagsByScope.mine.length === 0) {
            setMineDocs([]);
            return;
        }

        setLoading(true);
        setError('');

        try {
            let paginationToken = null;
            let nextToken = null;
            const collected = [];
            const seenTokens = new Set();

            do {
                if (paginationToken && seenTokens.has(paginationToken)) {
                    break;
                }
                if (paginationToken) {
                    seenTokens.add(paginationToken);
                }

                const params = {
                    userRoleUuid: user.selectedRole,
                    paginationToken,
                    paginationLimit: BULK_FETCH_LIMIT
                };

                const response = await listDocuments(params);

                const rows = Array.isArray(response?.data) ? response.data : [];
                collected.push(
                    ...rows.filter(
                        (doc) =>
                            doc?.uploader?.uuid === user.selectedRole &&
                            matchesSelectedTags(doc, selectedTagsByScope.mine)
                    )
                );

                nextToken = response?.headers?.['pagination-token'] || null;
                paginationToken = nextToken;
            } while (nextToken);

            setMineDocs(collected);
        } catch (err) {
            setMineDocs([]);
            setError(err?.response?.data?.description || 'Nao foi possivel carregar os teus documentos.');
        } finally {
            setLoading(false);
        }
    }, [selectedTagsByScope.mine, user.selectedRole]);

    const loadAssignedDocuments = useCallback(async () => {
        if (!user.selectedRole) return;

        if (!selectedCaregiverUuid) {
            setAssignedDocs([]);
            setLoading(false);
            setError('');
            return;
        }

        setLoading(true);
        setError('');

        try {
            let paginationToken = null;
            let nextToken = null;
            const collected = [];
            const seenTokens = new Set();

            do {
                if (paginationToken && seenTokens.has(paginationToken)) {
                    break;
                }
                if (paginationToken) {
                    seenTokens.add(paginationToken);
                }

                const response = await listDocuments({
                    userRoleUuid: user.selectedRole,
                    caregiverRoleUuid: selectedCaregiverUuid,
                    assignedOnly: true,
                    paginationToken,
                    paginationLimit: BULK_FETCH_LIMIT
                });

                const rows = Array.isArray(response?.data) ? response.data : [];
                collected.push(...rows.filter((doc) => matchesSelectedTags(doc, selectedTagsByScope.assigned)));

                nextToken = response?.headers?.['pagination-token'] || null;
                paginationToken = nextToken;
            } while (nextToken);

            setAssignedDocs(collected);
        } catch (err) {
            setAssignedDocs([]);
            setError(err?.response?.data?.description || 'Nao foi possivel carregar os documentos atribuídos.');
        } finally {
            setLoading(false);
        }
    }, [
        selectedCaregiverUuid,
        selectedTagsByScope.assigned,
        user.selectedRole
    ]);

    const loadTeamDocuments = useCallback(async () => {
        if (!user.selectedRole) return;

        if (!selectedTeamMemberUuid) {
            setTeamDocs([]);
            setLoading(false);
            setError('');
            return;
        }

        setLoading(true);
        setError('');

        try {
            let paginationToken = null;
            let nextToken = null;
            const collected = [];
            const seenTokens = new Set();

            do {
                if (paginationToken && seenTokens.has(paginationToken)) {
                    break;
                }
                if (paginationToken) {
                    seenTokens.add(paginationToken);
                }

                const response = await listDocuments({
                    userRoleUuid: user.selectedRole,
                    clinicianRoleUuid: selectedTeamMemberUuid,
                    paginationToken,
                    paginationLimit: BULK_FETCH_LIMIT
                });

                const rows = Array.isArray(response?.data) ? response.data : [];
                collected.push(...rows.filter((doc) => matchesSelectedTags(doc, selectedTagsByScope.team)));

                nextToken = response?.headers?.['pagination-token'] || null;
                paginationToken = nextToken;
            } while (nextToken);

            setTeamDocs(collected);
        } catch (err) {
            setTeamDocs([]);
            setError(err?.response?.data?.description || 'Nao foi possivel carregar os documentos da equipa.');
        } finally {
            setLoading(false);
        }
    }, [selectedTeamMemberUuid, selectedTagsByScope.team, user.selectedRole]);

    useEffect(() => {
        if (scope === MINE_SCOPE) {
            const effectKey = JSON.stringify({
                role: user.selectedRole || '',
                tags: selectedTagsByScope.mine || []
            });

            if (mineEffectKeyRef.current === effectKey) {
                return;
            }

            mineEffectKeyRef.current = effectKey;
            void loadMineDocuments();
        }
    }, [loadMineDocuments, scope, selectedTagsByScope.mine, user.selectedRole]);

    useEffect(() => {
        if (scope === ASSIGNED_SCOPE) {
            const effectKey = JSON.stringify({
                role: user.selectedRole || '',
                caregiver: selectedCaregiverUuid || '',
                tags: selectedTagsByScope.assigned || []
            });

            if (assignedEffectKeyRef.current === effectKey) {
                return;
            }

            assignedEffectKeyRef.current = effectKey;
            void loadAssignedDocuments();
        }
    }, [
        loadAssignedDocuments,
        scope,
        selectedCaregiverUuid,
        selectedTagsByScope.assigned,
        user.selectedRole
    ]);

    useEffect(() => {
        if (scope === TEAM_SCOPE) {
            const effectKey = JSON.stringify({
                role: user.selectedRole || '',
                clinician: selectedTeamMemberUuid || '',
                tags: selectedTagsByScope.team || []
            });

            if (teamEffectKeyRef.current === effectKey) {
                return;
            }

            teamEffectKeyRef.current = effectKey;
            void loadTeamDocuments();
        }
    }, [
        loadTeamDocuments,
        scope,
        selectedTagsByScope.team,
        selectedTeamMemberUuid,
        user.selectedRole
    ]);

    const refreshActiveScope = useCallback(async () => {
        if (scope === MINE_SCOPE) {
            await loadMineDocuments();
            return;
        }

        if (scope === ASSIGNED_SCOPE) {
            await loadAssignedDocuments();
            return;
        }

        await loadTeamDocuments();
    }, [loadAssignedDocuments, loadMineDocuments, loadTeamDocuments, scope]);

    const handleToggleTag = (tagUuid) => {
        setSelectedTagsByScope((prev) => {
            const current = prev[scope] || [];
            const exists = current.includes(tagUuid);
            return {
                ...prev,
                [scope]: exists ? current.filter((uuid) => uuid !== tagUuid) : [...current, tagUuid]
            };
        });
    };

    const handleClearTags = () => {
        setSelectedTagsForScope(scope, []);
    };

    const handleSelectAllTags = () => {
        setSelectedTagsForScope(scope, allTagFilterValues);
    };

    const handleOpenCreateTagDialog = () => {
        setTagError('');
        setNewTagName('');
        setCreateTagDialogOpen(true);
    };

    const handleCreateTagFromDialog = async () => {
        await createTag(newTagName, (created) => {
            setSelectedTagsByScope((prev) => ({
                ...prev,
                [scope]: (prev[scope] || []).includes(created.uuid)
                    ? prev[scope]
                    : [...(prev[scope] || []), created.uuid]
            }));
            setCreateTagDialogOpen(false);
            setNewTagName('');
        });
    };

    const handleCreateTagInUpload = async () => {
        await createTag(uploadNewTag, (created) => {
            setUploadTags((prev) => (prev.includes(created.uuid) ? prev : [...prev, created.uuid]));
            setUploadNewTag('');
        });
    };

    const handlePickedFile = (file) => {
        if (!file) return;
        setUploadError('');
        setUploadFileObj(file);
        setUploadDocumentName('');
        setUploadHidden(false);
        setUploadTags([]);
        setUploadNewTag('');
        setUploadDialogOpen(true);
    };

    const handleUpload = async () => {
        if (!user.selectedRole || !uploadFileObj) return;

        setUploading(true);
        setUploadError('');

        try {
            const uploadResp = await uploadDocument({
                userRoleUuid: user.selectedRole,
                file: uploadFileObj,
                tags: uploadTags,
                hidden: uploadHidden
            });

            const createdDocUuid = uploadResp?.data?.uuid || null;
            const requestedName = uploadDocumentName.trim();

            if (createdDocUuid && requestedName) {
                await updateDocument({
                    userRoleUuid: user.selectedRole,
                    documentUuid: createdDocUuid,
                    name: requestedName
                });
            }

            setUploadDialogOpen(false);
            setUploadFileObj(null);
            setUploadDocumentName('');
            setUploadHidden(false);
            setUploadTags([]);
            setUploadNewTag('');
            setMinePageIndex(0);
            await loadMineDocuments();
        } catch (err) {
            setUploadError(err?.response?.data?.description || 'Falha no upload do documento.');
        } finally {
            setUploading(false);
        }
    };

    const openDeleteDocumentDialog = (doc) => {
        setSelectedDocument(doc || null);
        setDocumentActionError('');
        setDeleteDialogOpen(true);
    };

    const handleDeleteSelectedDocument = async () => {
        if (!user.selectedRole || !selectedDocument?.uuid) return;

        setDeletingDocument(true);
        setDocumentActionError('');

        try {
            await deleteDocument({
                userRoleUuid: user.selectedRole,
                documentUuid: selectedDocument.uuid
            });

            setDeleteDialogOpen(false);
            setSelectedDocument(null);
            setMinePageIndex(0);
            await loadMineDocuments();
        } catch (err) {
            setDocumentActionError(err?.response?.data?.description || 'Nao foi possivel eliminar o documento.');
        } finally {
            setDeletingDocument(false);
        }
    };

    const handleDownload = async (doc) => {
        if (!user.selectedRole || !doc?.uuid) return;

        try {
            const resp = await downloadDocument({
                userRoleUuid: user.selectedRole,
                documentUuid: doc.uuid
            });
            const blob = new Blob([resp.data], {
                type: resp.headers?.['content-type'] || 'application/octet-stream'
            });
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = doc.name || `documento.${doc.extension || 'bin'}`;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(err?.response?.data?.description || 'Nao foi possivel descarregar o documento.');
        }
    };

    const openDocumentTagsDialog = (doc) => {
        setSelectedDocument(doc || null);
        setDocumentTagsEdit(Array.isArray(doc?.tags) ? doc.tags.map((tag) => tag.uuid) : []);
        setDocumentActionError('');
        setDocumentTagsDialogOpen(true);
    };

    const loadDocumentCaregivers = useCallback(
        async (docUuid) => {
            if (!user.selectedRole || !docUuid) return;

            setLoadingAssignedCaregivers(true);
            try {
                let paginationToken = null;
                let nextToken = null;
                const collected = [];
                const seenTokens = new Set();

                do {
                    if (paginationToken && seenTokens.has(paginationToken)) {
                        break;
                    }
                    if (paginationToken) {
                        seenTokens.add(paginationToken);
                    }

                    const response = await listDocumentCaregivers({
                        userRoleUuid: user.selectedRole,
                        documentUuid: docUuid,
                        paginationToken,
                        paginationLimit: 50
                    });

                    const rows = Array.isArray(response?.data) ? response.data : [];
                    collected.push(...rows);

                    nextToken = response?.headers?.['pagination-token'] || null;
                    paginationToken = nextToken;
                } while (nextToken);

                setDocumentAssignedCaregivers(collected);
            } catch {
                setDocumentAssignedCaregivers([]);
            } finally {
                setLoadingAssignedCaregivers(false);
            }
        },
        [user.selectedRole]
    );

    const openDocumentAssignDialog = (doc) => {
        setSelectedDocument(doc || null);
        setDocumentCaregiverUuid('');
        setDocumentActionError('');
        setDocumentAssignedCaregivers([]);
        setDocumentAssignDialogOpen(true);
        void loadDocumentCaregivers(doc?.uuid);
    };

    const handleSaveDocumentTags = async () => {
        if (!user.selectedRole || !selectedDocument?.uuid) return;

        setDocumentActionLoading(true);
        setDocumentActionError('');

        try {
            await updateDocumentTags({
                userRoleUuid: user.selectedRole,
                documentUuid: selectedDocument.uuid,
                tags: documentTagsEdit
            });
            setDocumentTagsDialogOpen(false);
            await refreshActiveScope();
        } catch (err) {
            setDocumentActionError(err?.response?.data?.description || 'Nao foi possivel atualizar tags.');
        } finally {
            setDocumentActionLoading(false);
        }
    };

    const handleAssignSelectedDocument = async () => {
        if (!user.selectedRole || !selectedDocument?.uuid || !documentCaregiverUuid) return;

        setDocumentActionLoading(true);
        setDocumentActionError('');

        try {
            await assignDocument({
                userRoleUuid: user.selectedRole,
                documentUuid: selectedDocument.uuid,
                caregiverRoleUuid: documentCaregiverUuid
            });
            setDocumentCaregiverUuid('');
            await loadDocumentCaregivers(selectedDocument.uuid);
            if (scope === ASSIGNED_SCOPE) {
                await loadAssignedDocuments();
            }
        } catch (err) {
            setDocumentActionError(err?.response?.data?.description || 'Nao foi possivel atribuir documento.');
        } finally {
            setDocumentActionLoading(false);
        }
    };

    const handleUnassignSelectedDocument = async (caregiverUuid) => {
        if (!user.selectedRole || !selectedDocument?.uuid || !caregiverUuid) return;

        setDocumentActionLoading(true);
        setDocumentActionError('');

        try {
            await unassignDocument({
                userRoleUuid: user.selectedRole,
                documentUuid: selectedDocument.uuid,
                caregiverRoleUuid: caregiverUuid
            });
            await loadDocumentCaregivers(selectedDocument.uuid);
            if (scope === ASSIGNED_SCOPE) {
                await loadAssignedDocuments();
            }
        } catch (err) {
            setDocumentActionError(err?.response?.data?.description || 'Nao foi possivel remover atribuicao.');
        } finally {
            setDocumentActionLoading(false);
        }
    };

    const currentCaregiverName = useMemo(
        () => caregivers.find((item) => item.userId === selectedCaregiverUuid)?.displayName || '',
        [caregivers, selectedCaregiverUuid]
    );

    const currentTeamMemberName = useMemo(
        () => teamMembers.find((item) => item.userId === selectedTeamMemberUuid)?.displayName || '',
        [selectedTeamMemberUuid, teamMembers]
    );

    const assignableCaregivers = useMemo(() => {
        const assignedUuids = new Set(
            (documentAssignedCaregivers || []).map((caregiver) => caregiver.uuid).filter(Boolean)
        );

        return caregivers.filter((caregiver) => !assignedUuids.has(caregiver.userId));
    }, [caregivers, documentAssignedCaregivers]);

    const renderTagFilters = (rightContent = null) => (
        <Stack spacing={1.2}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle2" fontWeight={700} color="#3D3D3D">
                    Filtrar por tags
                </Typography>
                <Stack direction="row" spacing={1.2} alignItems="center" flexWrap="wrap" useFlexGap>
                    {rightContent}
                    <Stack direction="row" spacing={0.8}>
                        <Tooltip title="Selecionar todas as tags">
                            <IconButton onClick={handleSelectAllTags} sx={filterActionButtonSx}>
                                <DoneAllIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Limpar seleção de tags">
                            <IconButton onClick={handleClearTags} sx={filterActionButtonSx}>
                                <RemoveDoneIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Criar nova tag">
                            <IconButton onClick={handleOpenCreateTagDialog} sx={filterActionButtonSx}>
                                <AddIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                    label="Sem tags"
                    clickable
                    onClick={() => handleToggleTag(NO_TAGS_FILTER)}
                    sx={{
                        borderRadius: '12px',
                        height: 38,
                        px: 0.6,
                        border: selectedTagUuids.includes(NO_TAGS_FILTER)
                            ? '2px solid #D63731'
                            : '2px solid #E2E2E2',
                        bgcolor: selectedTagUuids.includes(NO_TAGS_FILTER)
                            ? 'rgba(214, 55, 49, 0.05)'
                            : '#FFFFFF',
                        color: selectedTagUuids.includes(NO_TAGS_FILTER) ? '#D63731' : '#3D3D3D',
                        fontWeight: 700,
                        '& .MuiChip-label': {
                            px: 1.1
                        }
                    }}
                />
                {tags.map((tag) => {
                    const active = selectedTagUuids.includes(tag.uuid);
                    return (
                        <Chip
                            key={tag.uuid}
                            label={normalizeTagLabel(tag)}
                            clickable
                            onClick={() => handleToggleTag(tag.uuid)}
                            sx={{
                                borderRadius: '12px',
                                height: 38,
                                px: 0.6,
                                border: active ? '2px solid #D63731' : '2px solid #E2E2E2',
                                bgcolor: active ? 'rgba(214, 55, 49, 0.05)' : '#FFFFFF',
                                color: active ? '#D63731' : '#3D3D3D',
                                fontWeight: 700,
                                '& .MuiChip-label': {
                                    px: 1.1
                                }
                            }}
                        />
                    );
                })}

                {tags.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        Sem tags disponíveis.
                    </Typography>
                ) : null}
            </Stack>
        </Stack>
    );

    const renderDocumentRow = (doc) => (
        <Paper
            key={doc.uuid}
            sx={{
                px: 1.8,
                py: 1.35,
                display: 'grid',
                gap: 1.2,
                gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 2fr) minmax(0, 1.2fr) auto' },
                alignItems: 'center',
                bgcolor: '#ECECEC',
                borderRadius: '14px',
                boxShadow: 'none'
            }}
        >
            <Stack spacing={0.8} minWidth={0} justifyContent="center">
                <Typography variant="body1" fontWeight={700} color="#3D3D3D" noWrap>
                    {doc.name}
                </Typography>
                <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                    {doc.hidden ? (
                        <Chip
                            label="Interno"
                            size="small"
                            sx={{
                                alignSelf: 'center',
                                bgcolor: 'rgba(214, 55, 49, 0.10)',
                                border: '1px solid rgba(214, 55, 49, 0.28)',
                                color: '#D63731',
                                fontWeight: 700,
                                '& .MuiChip-label': {
                                    display: 'flex',
                                    alignItems: 'center'
                                }
                            }}
                        />
                    ) : null}
                    {(doc.tags || []).length > 0 ? (
                        doc.tags.map((tag) => (
                            <Chip
                                key={tag.uuid}
                                label={normalizeTagLabel(tag)}
                                size="small"
                                sx={{
                                    bgcolor: '#FFFFFF',
                                    border: '1px solid #E2E2E2',
                                    color: '#3D3D3D',
                                    fontWeight: 600
                                }}
                            />
                        ))
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            Sem tags
                        </Typography>
                    )}
                </Stack>
            </Stack>

            <Stack spacing={0.35} minWidth={0}>
                {scope === MINE_SCOPE ? (
                    <>
                        <Typography variant="body2" fontWeight={700} color="#3D3D3D">
                            Documento teu
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                            .{doc.extension || '--'}
                        </Typography>
                    </>
                ) : (
                    <>
                        <Typography variant="body2" fontWeight={700} color="#3D3D3D" noWrap>
                            {scope === ASSIGNED_SCOPE
                                ? currentCaregiverName || 'Cuidador selecionado'
                                : currentTeamMemberName || 'Membro da equipa selecionado'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {scope === ASSIGNED_SCOPE
                                ? `Upload de ${doc?.uploader?.name || '--'}`
                                : `Documento criado por ${doc?.uploader?.name || '--'}`}
                        </Typography>
                    </>
                )}
            </Stack>

            <Stack direction="row" spacing={0.8} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                <Tooltip title="Editar tags">
                    <IconButton onClick={() => openDocumentTagsDialog(doc)} sx={actionIconButtonSx}>
                        <Box component="img" src="/assets/logos-svg/tags.svg" alt="" sx={{ width: 18, height: 18 }} />
                    </IconButton>
                </Tooltip>
                {scope === MINE_SCOPE || scope === TEAM_SCOPE ? (
                    <Tooltip title="Atribuir documento">
                        <IconButton onClick={() => openDocumentAssignDialog(doc)} sx={actionIconButtonSx}>
                            <Box component="img" src="/assets/logos-svg/assign.svg" alt="" sx={{ width: 22, height: 22 }} />
                        </IconButton>
                    </Tooltip>
                ) : null}
                {scope === MINE_SCOPE ? (
                    <Tooltip title="Eliminar documento">
                        <IconButton onClick={() => openDeleteDocumentDialog(doc)} sx={actionIconButtonSx}>
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                ) : null}
                <Tooltip title="Transferir documento">
                    <IconButton onClick={() => void handleDownload(doc)} sx={actionIconButtonSx}>
                        <Box component="img" src="/assets/logos-svg/download.svg" alt="" sx={{ width: 22, height: 22 }} />
                    </IconButton>
                </Tooltip>
            </Stack>
        </Paper>
    );

    return (
        <Stack spacing={2} sx={{ height: '100%', minHeight: 0 }}>
            <Typography variant="h5" fontWeight={700} color="#3D3D3D">
                Documentos de Apoio
            </Typography>

            <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap>
                <Tooltip title="Ver os teus documentos">
                    <Button onClick={() => setScope(MINE_SCOPE)} sx={tabButtonSx(scope === MINE_SCOPE)}>
                        Meus Documentos
                    </Button>
                </Tooltip>
                <Tooltip title="Ver documentos criados pela equipa">
                    <Button onClick={() => setScope(TEAM_SCOPE)} sx={tabButtonSx(scope === TEAM_SCOPE)}>
                        Documentos da Equipa
                    </Button>
                </Tooltip>
                <Tooltip title="Ver documentos atribuídos a cuidadores">
                    <Button
                        onClick={() => setScope(ASSIGNED_SCOPE)}
                        sx={tabButtonSx(scope === ASSIGNED_SCOPE)}
                    >
                        Documentos Atribuídos
                    </Button>
                </Tooltip>
            </Stack>

            <Paper
                sx={{
                    ...sectionCardSx,
                    flex: 1,
                    height: { xs: 'auto', lg: 550 },
                    minHeight: { xs: 420, lg: 550 },
                    overflow: 'hidden'
                }}
            >
                {scope === MINE_SCOPE ? (
                    <Box
                        sx={{
                            height: '100%',
                            minHeight: 0,
                            display: 'grid',
                            gridTemplateRows: 'auto 1fr',
                            gap: 2
                        }}
                    >
                        {renderTagFilters()}

                        <Box
                            sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.7fr) minmax(300px, 0.95fr)' },
                            gap: 2,
                            height: '100%',
                            minHeight: 0
                        }}
                    >
                        <Stack spacing={1.4} sx={{ minHeight: 0 }}>
                            {loading ? (
                                <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                                    <CircularProgress size={26} />
                                </Box>
                            ) : error ? (
                                <Typography color="error">{error}</Typography>
                            ) : selectedTagUuids.length === 0 ? (
                                <Typography color="text.secondary">
                                    Nenhuma tag selecionada.
                                </Typography>
                            ) : activeDocs.length === 0 ? (
                                <Typography color="text.secondary">Sem documentos para mostrar.</Typography>
                            ) : (
                                <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.4 }}>
                                    <Stack spacing={1}>
                                        {activeDocs.map((doc) => renderDocumentRow(doc))}
                                    </Stack>
                                </Box>
                            )}

                            <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'flex-end', gap: 0.5, alignItems: 'center' }}>
                                <Tooltip title="Página anterior">
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={() => setMinePageIndex((prev) => Math.max(prev - 1, 0))}
                                            disabled={pageIndex <= 0}
                                        >
                                            <KeyboardArrowLeftIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ minWidth: 28, textAlign: 'center' }}
                                >
                                    {`${pageIndex + 1} de ${totalPages}`}
                                </Typography>
                                <Tooltip title="Página seguinte">
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                if (!hasNextPage) return;
                                                setMinePageIndex((prev) => prev + 1);
                                            }}
                                            disabled={!hasNextPage}
                                        >
                                            <KeyboardArrowRightIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        </Stack>

                        <Box
                            onClick={() => dropInputRef.current?.click()}
                            onDrop={(event) => {
                                event.preventDefault();
                                handlePickedFile(event.dataTransfer?.files?.[0] || null);
                            }}
                            onDragOver={(event) => event.preventDefault()}
                            sx={{
                                ...uploadDropzoneSx,
                                minHeight: { xs: 180, lg: '100%' }
                            }}
                        >
                            <Box component="img" src="/assets/logos-svg/upload.svg" alt="" sx={{ width: 30, height: 30 }} />
                            <Typography variant="body1" fontWeight={700} color="#3D3D3D">
                                Adicionar documento
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Arrasta um ficheiro para aqui ou clica para selecionar.
                            </Typography>
                            <input
                                ref={dropInputRef}
                                hidden
                                type="file"
                                onChange={(event) => handlePickedFile(event.target.files?.[0] || null)}
                            />
                        </Box>
                    </Box>
                </Box>
                ) : (
                    <Stack spacing={2} sx={{ height: '100%', minHeight: 0 }}>
                        {renderTagFilters(
                            scope === ASSIGNED_SCOPE ? (
                                <FormControl size="small" sx={{ minWidth: 260, maxWidth: 360, flexShrink: 0 }}>
                                    <InputLabel id="documents-caregiver-select-label">Cuidador</InputLabel>
                                    <Select
                                        labelId="documents-caregiver-select-label"
                                        label="Cuidador"
                                        value={selectedCaregiverUuid}
                                        onChange={(event) => setSelectedCaregiverUuid(event.target.value)}
                                        MenuProps={selectMenuProps}
                                    >
                                        {caregivers.map((item) => (
                                            <MenuItem key={item.userId} value={item.userId}>
                                                {item.displayName || item.userId}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            ) : scope === TEAM_SCOPE ? (
                                <FormControl size="small" sx={{ minWidth: 260, maxWidth: 360, flexShrink: 0 }}>
                                    <InputLabel id="documents-team-select-label">Membro da equipa</InputLabel>
                                    <Select
                                        labelId="documents-team-select-label"
                                        label="Membro da equipa"
                                        value={selectedTeamMemberUuid}
                                        onChange={(event) => setSelectedTeamMemberUuid(event.target.value)}
                                        MenuProps={selectMenuProps}
                                    >
                                        {teamMembers.map((item) => (
                                            <MenuItem key={item.userId} value={item.userId}>
                                                {item.displayName || item.userId}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            ) : null
                        )}

                        {loading ? (
                            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                                <CircularProgress size={26} />
                            </Box>
                        ) : error ? (
                            <Typography color="error">{error}</Typography>
                        ) : scope === ASSIGNED_SCOPE && !selectedCaregiverUuid ? (
                            <Typography color="text.secondary">
                                Seleciona um cuidador para veres os documentos atribuídos.
                            </Typography>
                        ) : scope === TEAM_SCOPE && !selectedTeamMemberUuid ? (
                            <Typography color="text.secondary">
                                Seleciona um membro da equipa para veres os documentos.
                            </Typography>
                        ) : selectedTagUuids.length === 0 ? (
                            <Typography color="text.secondary">
                                Nenhuma tag selecionada.
                            </Typography>
                        ) : activeDocs.length === 0 ? (
                            <Typography color="text.secondary">Sem documentos para mostrar.</Typography>
                        ) : (
                            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.4 }}>
                                <Stack spacing={1}>
                                    {activeDocs.map((doc) => renderDocumentRow(doc))}
                                </Stack>
                            </Box>
                        )}

                        <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'flex-end', gap: 0.5, alignItems: 'center' }}>
                            <Tooltip title="Página anterior">
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            if (scope === ASSIGNED_SCOPE) {
                                                setAssignedPageIndex((prev) => Math.max(prev - 1, 0));
                                                return;
                                            }

                                            setTeamPageIndex((prev) => Math.max(prev - 1, 0));
                                        }}
                                        disabled={pageIndex <= 0}
                                    >
                                        <KeyboardArrowLeftIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ minWidth: 28, textAlign: 'center' }}
                            >
                                {`${pageIndex + 1} de ${totalPages}`}
                            </Typography>
                            <Tooltip title="Página seguinte">
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            if (!hasNextPage) return;

                                            if (scope === ASSIGNED_SCOPE) {
                                                setAssignedPageIndex((prev) => prev + 1);
                                                return;
                                            }

                                            setTeamPageIndex((prev) => prev + 1);
                                        }}
                                        disabled={!hasNextPage}
                                    >
                                        <KeyboardArrowRightIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    </Stack>
                )}
            </Paper>

            <Dialog
                open={uploadDialogOpen}
                onClose={() => !uploading && setUploadDialogOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle sx={dialogTitleSx}>Upload de Documento</DialogTitle>
                <DialogContent sx={dialogContentSx}>
                    <Stack spacing={2}>
                        <Typography variant="body2" color="#3D3D3D">
                            Ficheiro: <strong>{uploadFileObj?.name || '--'}</strong>
                        </Typography>

                        <TextField
                            size="small"
                            fullWidth
                            label="Nome do documento"
                            placeholder={uploadFileObj?.name || 'Nome do documento'}
                            value={uploadDocumentName}
                            onChange={(event) => setUploadDocumentName(event.target.value)}
                            helperText="Se deixar em branco, fica com o nome original do ficheiro."
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={uploadHidden}
                                    onChange={(event) => setUploadHidden(event.target.checked)}
                                    color="error"
                                />
                            }
                            label="Documento interno (escondido do cuidador)"
                        />

                        <FormControl size="small" fullWidth>
                            <InputLabel id="docs-upload-tags-label">Tags</InputLabel>
                            <Select
                                labelId="docs-upload-tags-label"
                                label="Tags"
                                multiple
                                value={uploadTags}
                                onChange={(event) => {
                                    const value = event.target.value;
                                    setUploadTags(Array.isArray(value) ? value : []);
                                }}
                                renderValue={(selected) =>
                                    selected
                                        .map((uuid) => tags.find((tag) => tag.uuid === uuid)?.tag || uuid)
                                        .join(', ')
                                }
                            >
                                {tags.map((tag) => (
                                    <MenuItem key={tag.uuid} value={tag.uuid}>
                                        {normalizeTagLabel(tag)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <TextField
                                size="small"
                                fullWidth
                                label="Nova tag"
                                value={uploadNewTag}
                                onChange={(event) => setUploadNewTag(event.target.value)}
                            />
                            <Button
                                variant="outlined"
                                onClick={() => void handleCreateTagInUpload()}
                                disabled={tagSubmitting || !uploadNewTag.trim()}
                                sx={secondaryDialogButtonSx}
                            >
                                Criar tag
                            </Button>
                        </Stack>

                        {uploadError ? (
                            <Typography variant="caption" color="error">
                                {uploadError}
                            </Typography>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions sx={dialogActionsSx}>
                    <Button
                        onClick={() => {
                            setUploadDialogOpen(false);
                            setUploadFileObj(null);
                            setUploadDocumentName('');
                            setUploadHidden(false);
                            setUploadTags([]);
                            setUploadNewTag('');
                            setUploadError('');
                        }}
                        disabled={uploading}
                        variant="outlined"
                        sx={secondaryDialogButtonSx}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => void handleUpload()}
                        disabled={uploading}
                        sx={primaryDialogButtonSx}
                    >
                        {uploading ? 'A carregar...' : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={createTagDialogOpen}
                onClose={() => setCreateTagDialogOpen(false)}
                fullWidth
                maxWidth="xs"
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle sx={dialogTitleSx}>Criar Nova Tag</DialogTitle>
                <DialogContent sx={dialogContentSx}>
                    <Stack spacing={2}>
                        <TextField
                            size="small"
                            fullWidth
                            label="Nome da tag"
                            value={newTagName}
                            onChange={(event) => setNewTagName(event.target.value)}
                        />
                        {tagError ? (
                            <Typography variant="caption" color="error">
                                {tagError}
                            </Typography>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions sx={dialogActionsSx}>
                    <Button onClick={() => setCreateTagDialogOpen(false)} variant="outlined" sx={secondaryDialogButtonSx}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={() => void handleCreateTagFromDialog()}
                        disabled={tagSubmitting || !newTagName.trim()}
                        variant="contained"
                        sx={primaryDialogButtonSx}
                    >
                        Criar tag
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteDialogOpen}
                onClose={() => !deletingDocument && setDeleteDialogOpen(false)}
                fullWidth
                maxWidth="xs"
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle sx={dialogTitleSx}>Eliminar Documento</DialogTitle>
                <DialogContent sx={dialogContentSx}>
                    <Stack spacing={1.5}>
                        <Typography variant="body2" color="#3D3D3D">
                            Tem a certeza que pretende eliminar o documento <strong>{selectedDocument?.name || '--'}</strong>?
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Não será possivel eliminar o documento, se este ainda estiver atribuido a cuidadores.
                        </Typography>
                        {documentActionError ? (
                            <Typography variant="caption" color="error">
                                {documentActionError}
                            </Typography>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions sx={dialogActionsSx}>
                    <Button
                        onClick={() => setDeleteDialogOpen(false)}
                        disabled={deletingDocument}
                        variant="outlined"
                        sx={secondaryDialogButtonSx}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={() => void handleDeleteSelectedDocument()}
                        disabled={deletingDocument}
                        variant="contained"
                        sx={{
                            ...primaryDialogButtonSx,
                            bgcolor: '#D63731',
                            '&:hover': {
                                bgcolor: '#C4312B',
                                boxShadow: 'none'
                            }
                        }}
                    >
                        {deletingDocument ? 'A eliminar...' : 'Eliminar'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={documentTagsDialogOpen}
                onClose={() => setDocumentTagsDialogOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle sx={dialogTitleSx}>Editar Tags do Documento</DialogTitle>
                <DialogContent sx={dialogContentSx}>
                    <Stack spacing={2}>
                        <Typography variant="body2" color="#3D3D3D">
                            Nome: <strong>{selectedDocument?.name || '--'}</strong>
                        </Typography>

                        <Stack spacing={1.2}>
                            <Typography variant="subtitle2" fontWeight={700} color="#3D3D3D">
                                Tags
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {tags.map((tag) => {
                                    const active = documentTagsEdit.includes(tag.uuid);
                                    return (
                                        <Chip
                                            key={tag.uuid}
                                            label={normalizeTagLabel(tag)}
                                            clickable
                                            onClick={() =>
                                                setDocumentTagsEdit((prev) =>
                                                    prev.includes(tag.uuid)
                                                        ? prev.filter((uuid) => uuid !== tag.uuid)
                                                        : [...prev, tag.uuid]
                                                )
                                            }
                                            sx={{
                                                borderRadius: '12px',
                                                height: 38,
                                                px: 0.6,
                                                border: active ? '2px solid #D63731' : '2px solid #E2E2E2',
                                                bgcolor: active ? 'rgba(214, 55, 49, 0.05)' : '#FFFFFF',
                                                color: active ? '#D63731' : '#3D3D3D',
                                                fontWeight: 700,
                                                '& .MuiChip-label': {
                                                    px: 1.1
                                                }
                                            }}
                                        />
                                    );
                                })}

                                {tags.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        Sem tags disponiveis.
                                    </Typography>
                                ) : null}
                            </Stack>
                        </Stack>

                        {documentActionError ? (
                            <Typography variant="caption" color="error">
                                {documentActionError}
                            </Typography>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions sx={dialogActionsSx}>
                    <Button
                        onClick={() => setDocumentTagsDialogOpen(false)}
                        variant="outlined"
                        sx={secondaryDialogButtonSx}
                    >
                        Fechar
                    </Button>
                    <Button
                        onClick={() => void handleSaveDocumentTags()}
                        disabled={documentActionLoading}
                        variant="contained"
                        sx={primaryDialogButtonSx}
                    >
                        Guardar tags
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={documentAssignDialogOpen}
                onClose={() => setDocumentAssignDialogOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle sx={dialogTitleSx}>Atribuir Documento</DialogTitle>
                <DialogContent sx={dialogContentSx}>
                    <Stack spacing={2}>
                        <Typography variant="body2" color="#3D3D3D">
                            Nome: <strong>{selectedDocument?.name || '--'}</strong>
                        </Typography>

                        <FormControl size="small" fullWidth>
                            <InputLabel id="docs-assign-caregiver-label">Cuidador</InputLabel>
                            <Select
                                labelId="docs-assign-caregiver-label"
                                label="Cuidador"
                                value={documentCaregiverUuid}
                                onChange={(event) => setDocumentCaregiverUuid(event.target.value)}
                            >
                                {assignableCaregivers.map((item) => (
                                    <MenuItem key={item.userId} value={item.userId}>
                                        {item.displayName || item.userId}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {!assignableCaregivers.length ? (
                            <Typography variant="body2" color="text.secondary">
                                Todos os cuidadores disponíveis já têm este documento atribuído.
                            </Typography>
                        ) : null}

                        <Button
                            variant="contained"
                            onClick={() => void handleAssignSelectedDocument()}
                            disabled={
                                documentActionLoading ||
                                !documentCaregiverUuid ||
                                !assignableCaregivers.length
                            }
                            sx={primaryDialogButtonSx}
                        >
                            Atribuir
                        </Button>

                        <Box>
                            <Typography variant="subtitle2" fontWeight={700} color="#3D3D3D" sx={{ mb: 1 }}>
                                Cuidadores atribuídos
                            </Typography>
                            {loadingAssignedCaregivers ? (
                                <CircularProgress size={20} />
                            ) : documentAssignedCaregivers.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    Sem cuidadores atribuídos.
                                </Typography>
                            ) : (
                                <Stack spacing={1}>
                                    {documentAssignedCaregivers.map((caregiver) => (
                                        <Paper
                                            key={caregiver.uuid}
                                            sx={{
                                                px: 1.4,
                                                py: 1.1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                bgcolor: '#ECECEC',
                                                boxShadow: 'none',
                                                borderRadius: '12px'
                                            }}
                                        >
                                            <Typography variant="body2" fontWeight={700} color="#3D3D3D">
                                                {caregiver.name}
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                onClick={() => void handleUnassignSelectedDocument(caregiver.uuid)}
                                                disabled={documentActionLoading}
                                                sx={secondaryDialogButtonSx}
                                            >
                                                Remover
                                            </Button>
                                        </Paper>
                                    ))}
                                </Stack>
                            )}
                        </Box>

                        {documentActionError ? (
                            <Typography variant="caption" color="error">
                                {documentActionError}
                            </Typography>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions sx={dialogActionsSx}>
                    <Button
                        onClick={() => setDocumentAssignDialogOpen(false)}
                        variant="outlined"
                        sx={secondaryDialogButtonSx}
                    >
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default DocumentsWorkspace;
